import { getSchedulesForClubs, SCHEDULE_FIELDS } from '@/api/schedule/scheduleApi';
import type { ClubSchedulesSlice } from '../engine/types';

/**
 * Earliest / latest tournament dates from selected reusable schedule IDs (wizard model).
 */
export async function fetchTournamentDatesFromClubSchedulesSlice(
  slice: ClubSchedulesSlice | null,
): Promise<{ startDate: Date; endDate: Date } | null> {
  if (!slice?.clubSchedules?.length) return null;

  const source = slice.clubSchedules;
  const allScheduleIds = new Set(source.flatMap((cs) => cs.scheduleIds));
  if (allScheduleIds.size === 0) return null;

  const clubIds = Array.from(new Set(source.map((cs) => cs.club.id)));
  const results = await getSchedulesForClubs(clubIds, SCHEDULE_FIELDS.DEFAULT);

  const allSelectedDates: string[] = [];
  results.forEach(({ schedules }) => {
    schedules
      .filter((s) => allScheduleIds.has(s.id))
      .forEach((s) => {
        s.schedule?.forEach((entry) => {
          if (entry.date) allSelectedDates.push(entry.date);
        });
      });
  });

  if (allSelectedDates.length === 0) return null;

  const sorted = [...allSelectedDates].sort();
  return {
    startDate: new Date(`${sorted[0]}T00:00:00`),
    endDate: new Date(`${sorted[sorted.length - 1]}T00:00:00`),
  };
}

/**
 * When schedule blocks cannot yield dates, matches tournament creation (wizard):
 * start = day after signup deadline, end = +30 days from that start.
 * If no deadline is provided, uses today's calendar date as the reference day (same as wizard).
 */
export function fallbackTournamentDatesFromSignupDeadline(
  signupDeadlineDate: Date | null | undefined,
): { startDate: Date; endDate: Date } {
  const deadline = signupDeadlineDate ? new Date(signupDeadlineDate) : new Date();
  const startDate = new Date(deadline);
  startDate.setDate(startDate.getDate() + 1);

  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 30);

  return { startDate, endDate };
}

/**
 * Same rules as tournament creation in the wizard: prefer min/max dates from selected
 * reusable schedules; on empty result or fetch error, use
 * {@link fallbackTournamentDatesFromSignupDeadline}.
 */
export async function resolveTournamentDatesFromClubSchedulesSlice(
  slice: ClubSchedulesSlice | null,
  signupDeadlineDate: Date | null | undefined,
): Promise<{ startDate: Date; endDate: Date }> {
  try {
    const fromSchedules = await fetchTournamentDatesFromClubSchedulesSlice(slice);
    if (fromSchedules) return fromSchedules;
  } catch (e) {
    console.error('Error calculating tournament dates from schedules:', e);
  }
  return fallbackTournamentDatesFromSignupDeadline(signupDeadlineDate);
}
